/**
 * The live camera and QR reading.
 *
 * iPhone Safari has no built-in BarcodeDetector, so frames are decoded with jsQR (pure JS,
 * works offline, no wasm to cache). Frames are sampled about 10 times a second at reduced
 * resolution: plenty for a sticker at arm's length, gentle on the battery.
 */
import jsQR from "jsqr";
import { useCallback, useEffect, useRef, useState } from "react";

export type CameraStatus = "needs-permission" | "starting" | "live" | "denied" | "unavailable" | "error";

const GRANTED_KEY = "rotations:camera-granted";
const SAMPLE_EVERY_MS = 100;
const SAMPLE_WIDTH = 480;

/** Missing on desktop browsers without a camera API and on plain-HTTP origins. */
function cameraSupported(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";
}

function rememberGranted(): void {
  try {
    localStorage.setItem(GRANTED_KEY, "1");
  } catch {
    // fine: she'll just see the explainer again
  }
}

function wasGranted(): boolean {
  try {
    return localStorage.getItem(GRANTED_KEY) === "1";
  } catch {
    return false;
  }
}

export function useQrScanner(onDecode: (text: string) => void) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;
  const [status, setStatus] = useState<CameraStatus>(() => (cameraSupported() ? "needs-permission" : "unavailable"));

  const start = useCallback(async () => {
    if (!cameraSupported()) {
      setStatus("unavailable");
      return;
    }
    setStatus("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
      rememberGranted();
      setStatus("live");
    } catch (error) {
      const name = (error as { name?: string }).name;
      setStatus(name === "NotAllowedError" || name === "SecurityError" ? "denied" : name === "NotFoundError" ? "unavailable" : "error");
    }
  }, []);

  // Start straight away when permission was given before (iOS may still confirm).
  useEffect(() => {
    if (status === "needs-permission" && wasGranted()) void start();
  }, [status, start]);

  // Decode loop while live.
  useEffect(() => {
    if (status !== "live") return;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    let frame = 0;
    let last = 0;
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      const video = videoRef.current;
      if (!ctx || !video || video.readyState < 2 || now - last < SAMPLE_EVERY_MS) return;
      last = now;
      const scale = SAMPLE_WIDTH / (video.videoWidth || SAMPLE_WIDTH);
      canvas.width = SAMPLE_WIDTH;
      canvas.height = Math.round((video.videoHeight || SAMPLE_WIDTH) * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(image.data, image.width, image.height, { inversionAttempts: "dontInvert" });
      if (code?.data) onDecodeRef.current(code.data);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [status]);

  // Release the camera when the screen goes away.
  useEffect(
    () => () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  return { videoRef, status, start };
}
